/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useLayoutEffect, useRef } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";

import BlurText from "@/components/customCopmonent/Text/BlurText";

/**
 * Smallest the hero title is allowed to get while trying to fit on one line.
 * Below this it stops shrinking and is allowed to wrap, because a headline that
 * keeps shrinking to avoid a second line eventually reads as body copy.
 */
const MIN_TITLE_PX = 28;

/** Titles only get the one-line treatment from the `sm` breakpoint up. */
const FIT_FROM_PX = 640;

/**
 * Shrinks a hero title until it occupies a single line.
 *
 * Long page names ("Plastic Injection Molding & Machine tools") wrapped to two
 * lines at the fixed `sm:text-5xl`, which left the band looking top-heavy and
 * pushed the tagline and tags down. Sizing is measured rather than guessed from
 * character count: the same title is a different width in every locale, and
 * German and French run noticeably longer than English.
 *
 * `BlurText` renders each word as an inline-block inside a flex-wrap container,
 * so "does it wrap?" is simply whether the last word sits lower than the first
 * — no line-height arithmetic, and correct even mid-animation.
 */
function fitTitleToOneLine(el: HTMLElement) {
	el.style.fontSize = "";

	if (window.innerWidth < FIT_FROM_PX) return;

	const words = el.children;
	if (words.length < 2) return;

	const wraps = () =>
		(words[words.length - 1] as HTMLElement).offsetTop >
		(words[0] as HTMLElement).offsetTop;

	let size = parseFloat(getComputedStyle(el).fontSize);
	while (wraps() && size > MIN_TITLE_PX) {
		size -= 1;
		el.style.fontSize = `${size}px`;
	}
}

/** Shared className for the standardized blue hero band used on every page.
 *
 * Applied to the page's `SectionWrapper`, it is the single source of truth for
 * hero proportions site-wide. Everything from `sm:` up reproduces the previous
 * `pt-28 sm:pt-32` + wrapper `sm:py-14` box exactly, so desktop and large-tablet
 * rendering is byte-for-byte unchanged; only the sub-640px band is retuned so
 * the header stops eating half the viewport on a phone. */
export const PAGE_HERO_CLASS =
	"relative bg-[#3b0092] pt-20 pb-8 min-h-[280px] sm:min-h-0 sm:pt-32 sm:pb-14";

export function PageHeaderHero({
	title,
	description,
	meta,
	align = "left",
	className,
	titleAs = "p",
}: {
	eyebrow?: string;
	title: string;
	description?: string;
	meta?: string[];
	align?: "left" | "center";
	className?: string;
	/**
	 * Element the hero title renders as. Left as a paragraph by default so
	 * existing pages are untouched; pages that want the band to be their
	 * document headline pass `"h1"`.
	 */
	titleAs?: "p" | "h1";
}) {
	const alignClass =
		align === "center" ? "mx-auto text-center items-center" : "items-start";

	const titleRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const el = titleRef.current?.querySelector<HTMLElement>(".blur-text");
		if (!el) return;

		const fit = () => fitTitleToOneLine(el);
		fit();

		// Web fonts land after first paint and change every measurement, so fit
		// again once the real face is in use.
		void document.fonts?.ready.then(fit);

		let frame = 0;
		const onResize = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(fit);
		};
		window.addEventListener("resize", onResize);
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener("resize", onResize);
		};
	}, [title]);

	const childVariants = {
		hidden: { opacity: 0, y: 18, filter: "blur(4px)" },
		visible: {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] as const },
		},
	};

	return (
		<motion.div
			initial="hidden"
			animate="visible"
			variants={{
				hidden: {},
				visible: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
			}}
			className={cn(
				// Wider than the old `max-w-3xl` so long titles have room to sit on
				// one line at a decent size — Spanish and Italian page names run up
				// to 55 characters against English's 41. The prose below is pegged
				// back to 3xl so its reading measure is unchanged.
				"flex max-w-5xl flex-col gap-2.5 sm:gap-4",
				alignClass,
				className,
			)}>
			<div
				ref={titleRef}
				className="w-full">
				<BlurText
					as={titleAs}
					text={title}
					delay={200}
					animateBy="words"
					direction="top"
					className="text-3xl sm:text-5xl font-bold text-white"
				/>
			</div>

			{description && (
				<div className="max-w-3xl">
					<BlurText
						text={description}
						delay={200}
						animateBy="words"
						direction="top"
						className="text-sm sm:text-lg font-semibold text-white/70"
					/>
				</div>
			)}

			{/* Meta tags */}
			{meta && meta.length > 0 && (
				<motion.div
					variants={childVariants}
					className="flex flex-wrap gap-1.5 sm:gap-2">
					{meta.map((tag) => (
						<span
							key={tag}
							className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500 shadow-sm sm:px-2.5 sm:py-1 sm:text-[11px]">
							{tag}
						</span>
					))}
				</motion.div>
			)}
		</motion.div>
	);
}
